import { Cell } from '@tanstack/react-table';
import { VirtualItem } from 'react-virtual';
import { VIRTUAL_ROW_HEIGHT } from './common';

/**
 * Check if cell should be visible when table is collapsed on mobile
 * @param {Cell} cell - A Cell instance to check
 * @returns {boolean} true if cell is either a mobile header, select column, or grouping column
 */
export function isCellMobileVisibleCollapsed<T>(cell: Cell<T, unknown>) {
  return (
    getCellIsMobileHeader(cell) ||
    getCellIsSelectColumn(cell) ||
    getIsCellGroupingColumn(cell)
  );
}

/**
 * Check if cell should be visible when table is expanded on mobile
 * @param {Cell} cell - A Cell instance to check
 * @returns {boolean} true if cell is not a hidden, mobile header, select column, or grouping column
 */
export function isCellMobileVisibleExpanded<T>(cell: Cell<T, unknown>) {
  return !getIsCellHidden(cell) && !isCellMobileVisibleCollapsed(cell);
}

/**
 * Check if cell is mobile header
 * @param {Cell} cell - A Cell instance to check
 * @returns {boolean} true if cell is a mobile header
 */
function getCellIsMobileHeader<T>(cell: Cell<T, unknown>) {
  return cell.column.columnDef.meta?.isMobileHeader === true;
}

/**
 * Check if cell is a select column
 * @param {Cell} cell - A Cell instance to check
 * @returns {boolean} true if cell is a select column
 */
function getCellIsSelectColumn<T>(cell: Cell<T, unknown>) {
  return cell.column.columnDef.meta?.isSelectColumn === true;
}

/**
 * Check if cell is a grouping column
 * @param {Cell} cell - A Cell instance to check
 * @returns {boolean} true if cell is a grouping column
 */
function getIsCellGroupingColumn<T>(cell: Cell<T, unknown>) {
  return cell.column.columnDef.meta?.isGroupingColumn === true;
}

/**
 * Check if cell is hidden
 * @param {Cell} cell - A Cell instance to check
 * @returns {boolean} true if cell is hidden
 */
function getIsCellHidden<T>(cell: Cell<T, unknown>) {
  return cell.column.columnDef.meta?.hidden === true;
}

/**
 * This function calculates the spacing for the table container based on the height of the table container and the height of the virtual rows.
 * always keeping one row height as a buffer.
 * @param vRows virtual rows received from the react-virtual hook
 * @param tableRef a ref used on the table container
 * @returns the spacing for the table container used to fill the remaining space
 */
export function calculateSpacing(
  vRows: VirtualItem[],
  tableRef: React.RefObject<HTMLDivElement>,
): number {
  const virtualRowHeight = VIRTUAL_ROW_HEIGHT;
  const totalVirtualRowsHeight = vRows.length * virtualRowHeight;
  const tableContainerHeight = tableRef.current?.clientHeight ?? 0;
  const doesTableContainerHaveHeight = tableContainerHeight !== 0;
  // Calculate the remaining space within table container plus a row height
  const remainingSpaceInTableContainer =
    tableContainerHeight - totalVirtualRowsHeight + virtualRowHeight;
  // Return remaining space if the table container has height, else return 0
  const spacingForTableContainer = doesTableContainerHaveHeight
    ? remainingSpaceInTableContainer
    : 0;
  return spacingForTableContainer;
}
